import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Navigate, useLocation } from 'react-router-dom'
import { TopNavigation } from '@/components/TopNavigation'
import { Toast } from '@/components/Toast'
import { ColorSquare } from '@/components/ColorSquare'
import { TestDeviceIcon } from '@/components/TestDeviceIcon'
import { Submenu } from '@/components/Submenu'
import { PatientId } from '@/components/PatientId'
import { useAuth } from '@/hooks/useAuth'
import {
  ClinicianService,
  StudyService,
  DeviceService,
  UserProfileHelper
} from '@lura/shared'
import type {
  UserProfile,
  AssignedPatient,
  StudyWithAssignment,
  StudyDevice
} from '@lura/shared'

export const ClinicianDetailPage: React.FC = () => {
  const { clinicianId } = useParams<{ clinicianId: string }>()
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [clinician, setClinician] = useState<UserProfile | null>(null)
  const [assignedPatients, setAssignedPatients] = useState<AssignedPatient[]>(
    []
  )
  const [clinicianStudies, setClinicianStudies] = useState<
    StudyWithAssignment[]
  >([])
  const [clinicianDevices, setClinicianDevices] = useState<StudyDevice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const submenuItems = [
    { id: 'studies', label: 'Studies', href: `/clinicians/${clinicianId}` },
    {
      id: 'devices',
      label: 'Devices',
      href: `/clinicians/${clinicianId}/devices`
    },
    {
      id: 'patients',
      label: 'Patients',
      href: `/clinicians/${clinicianId}/patients`
    }
  ]

  // Derive active tab from URL
  const activeTab = location.pathname.endsWith('/devices')
    ? 'devices'
    : location.pathname.endsWith('/patients')
      ? 'patients'
      : 'studies'

  // Study Admin access check
  if (!UserProfileHelper.isStudyAdmin(user)) {
    return <Navigate to="/studies" replace />
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!clinicianId) {
        setError('Clinician ID is required')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Fetch clinician profile
        const clinicianProfile =
          await ClinicianService.getClinicianProfile(clinicianId)
        if (!clinicianProfile) {
          setError('Clinician not found')
          setLoading(false)
          return
        }
        setClinician(clinicianProfile)

        // Fetch patients who have used devices assigned to this clinician
        const patients =
          await ClinicianService.getClinicianPatients(clinicianId)
        setAssignedPatients(patients)

        // Fetch studies assigned to this clinician
        const studies = await StudyService.getClinicianStudies(clinicianId)
        setClinicianStudies(studies)

        // Fetch devices from all studies the clinician is assigned to
        const allDevices: StudyDevice[] = []
        for (const study of studies) {
          const studyDevices = await DeviceService.getStudyDevices(study.id)
          allDevices.push(...studyDevices)
        }
        setClinicianDevices(allDevices)
      } catch (err) {
        console.error('Error fetching data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [clinicianId])

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  return (
    <div className="min-h-screen bg-off-white">
      <TopNavigation
        currentPage="Clinicians"
        onSignOut={handleSignOut}
        user={user}
      />

      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="px-4 pb-6 sm:px-0">
          {/* Back Button */}
          <button
            onClick={() => navigate('/clinicians')}
            className="mb-4 flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Clinicians
          </button>

          {/* Page Header */}
          {clinician && (
            <div className="mb-8">
              <h2
                className="text-5xl font-normal text-dark-blue"
                style={{ fontFamily: 'Family' }}
              >
                {clinician.first_name} {clinician.last_name}
              </h2>
              <p className="mt-2 text-sm text-gray-600">{clinician.email}</p>
            </div>
          )}

          {/* Submenu */}
          {clinician && (
            <div className="mb-8">
              <Submenu items={submenuItems} activeItem={activeTab} />
            </div>
          )}

          {/* Error State for page-level errors (loading data) */}
          {error && (
            <div className="mb-6 rounded-3xl bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          )}

          {/* Main Content */}
          {!loading && clinician && (
            <div className="space-y-6">
              {activeTab === 'studies' && (
                <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Assigned Studies
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Studies assigned to this clinician
                    </p>
                  </div>
                  {clinicianStudies.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="text-sm">No studies assigned yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-highlight-green">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                              Study Title
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                              Description
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                              Created Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                              Clinicians
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                              Devices
                            </th>
                            <th className="relative px-6 py-3">
                              <span className="sr-only">View</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {clinicianStudies.map(study => (
                            <tr
                              key={study.id}
                              onClick={() => navigate(`/studies/${study.id}`)}
                              className="hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-3">
                                  <ColorSquare color={study.color} size="md" />
                                  <div className="text-sm font-medium text-gray-900">
                                    {study.title}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-500 max-w-md truncate">
                                  {study.description || '—'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">
                                  {new Date(
                                    study.created_at
                                  ).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                                  {study.clinician_count}{' '}
                                  {study.clinician_count === 1
                                    ? 'Clinician'
                                    : 'Clinicians'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                  {study.device_count}{' '}
                                  {study.device_count === 1
                                    ? 'Device'
                                    : 'Devices'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex justify-end">
                                  <svg
                                    className="w-5 h-5 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 5l7 7-7 7"
                                    />
                                  </svg>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'devices' && (
                <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Clinician Devices
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Devices available to this clinician
                    </p>
                  </div>

                  {clinicianDevices.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-sm">
                        No devices in assigned studies yet
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-highlight-green">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                              Device
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                              Most Recent User
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {clinicianDevices.map(device => (
                            <tr key={device.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900">
                                    {device.serial_number}
                                  </span>
                                  {device.is_test_device && <TestDeviceIcon />}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {device.most_recent_user_id ? (
                                  <>
                                    <div className="text-sm font-medium text-gray-900">
                                      <PatientId
                                        patientId={device.most_recent_user_id}
                                      />
                                    </div>
                                    {device.last_used_at && (
                                      <div className="text-xs text-gray-500">
                                        {new Date(
                                          device.last_used_at
                                        ).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-sm text-gray-400">
                                    Not yet used
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'patients' && (
                <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Clinician Patients
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Patients assigned to this clinician
                    </p>
                  </div>

                  {assignedPatients.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                      <p className="text-sm">
                        No patients have used devices in this clinician's
                        studies yet
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-highlight-green">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                              Patient
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                              Study
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider">
                              Devices
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider w-12"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {assignedPatients.map(patient => (
                            <tr
                              key={patient.id}
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => navigate(`/patient/${patient.id}`)}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    <PatientId patientId={patient.id} />
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Last active:{' '}
                                    {new Date(
                                      patient.last_activity
                                    ).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="space-y-2">
                                  {patient.devices_used.map(
                                    (deviceUsage, idx) => (
                                      <div
                                        key={idx}
                                        className="flex items-center gap-2 text-sm"
                                      >
                                        <ColorSquare
                                          color={deviceUsage.study_color}
                                          size="sm"
                                        />
                                        <span className="font-medium text-gray-900">
                                          {deviceUsage.study_title}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">
                                  {patient.devices_used.length > 0 ? (
                                    <div className="space-y-1">
                                      {patient.devices_used.map(
                                        (deviceUsage, idx) => (
                                          <div key={idx}>
                                            {deviceUsage.device_serial}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <svg
                                  className="w-5 h-5 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Toast Notifications */}
      <Toast
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />
      <Toast
        message={errorMessage}
        type="error"
        onClose={() => setErrorMessage(null)}
      />
    </div>
  )
}
